import type { Blocker, Transition } from "history";
import { useCallback, useContext, useEffect, useState } from "react";
import { Navigator, UNSAFE_NavigationContext } from "react-router-dom";
import ConfirmDialog from "../../../../components/dialogs/confirm-dialog";

type BlockerNavigator = Navigator & {
  location: Location;
  block(blocker: Blocker): () => void;
};

type Props = {
  hasUnsavedChanges: boolean;
};

const UnsavedChangesAlertModal = ({ hasUnsavedChanges }: Props) => {
  const { navigator } = useContext(UNSAFE_NavigationContext);

  const [displayAlert, setDisplayAlert] = useState(false);
  const [transition, setTransition] = useState<Transition>();

  useEffect(() => {
    if (!hasUnsavedChanges) {
      return;
    }

    const {
      block,
      location: { pathname },
    } = navigator as BlockerNavigator;

    const unblock = block((transition) => {
      const {
        location: { pathname: targetPathname },
      } = transition;

      // Note: We don't want to show the alert if the user is navigating to the same page.
      if (targetPathname === pathname) {
        return;
      }

      setDisplayAlert(true);

      setTransition({
        ...transition,
        retry() {
          unblock();
          transition.retry();
        },
      });
    });

    return unblock;
  }, [navigator, hasUnsavedChanges]);

  const leavePage = useCallback(() => {
    transition?.retry();
    setDisplayAlert(false);
  }, [transition]);

  const stayOnPage = useCallback(() => {
    setDisplayAlert(false);
  }, [setDisplayAlert]);

  return (
    <ConfirmDialog
      isOpen={displayAlert}
      title={"Warning"}
      description={"You have unsaved changes. Are you sure you want to leave?"}
      acceptText="YES"
      rejectText="NO"
      toggle={stayOnPage}
      onSave={(value) => {
        value ? leavePage() : stayOnPage();
      }}
    />
  );
};

export default UnsavedChangesAlertModal;
